import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { nav, site, waLink } from "@/content/siteContent";
import { scrollToSection } from "@/lib/smoothScroll";

const Logo = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-3" data-testid="logo-button">
    <img src="/images/logo-icon.png" alt="Logo Sagarelo" className="h-10 w-10 rounded-xl border border-metal object-cover" />
    <span className="flex flex-col items-start leading-none">
      <span className="font-display text-base font-extrabold tracking-wide text-paper">
        {site.name.replace("®", "")}
        <sup className="relative -left-1 align-super text-[0.62em] font-bold text-brand">®</sup>
      </span>
      <span className="text-[0.58rem] font-bold tracking-[0.32em] text-brand">{site.suffix}</span>
    </span>
  </button>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      window.location.href = href;
      return;
    }
    if (window.location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    setTimeout(() => scrollToSection(href), open ? 250 : 0);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${scrolled ? "header-scrolled" : "bg-transparent"}`}
        data-testid="main-header"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Logo onClick={() => go("#inicio")} />

          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {nav.map((item) => (
              <button
                key={item.href}
                onClick={() => go(item.href)}
                className={item.href === "/contratar"
                  ? "whitespace-nowrap rounded-full border border-brand/70 bg-brand/10 px-4 py-2 text-[0.72rem] font-extrabold uppercase tracking-[0.1em] text-brand transition-[background-color,color,border-color] duration-300 hover:bg-brand hover:text-ink"
                  : "text-[0.72rem] font-bold uppercase tracking-[0.1em] text-mist transition-colors duration-300 hover:text-brand"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-industrial !hidden !px-5 !py-2.5 lg:!inline-flex"
              data-testid="header-quote-button"
            >
              Solicitar Orçamento
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              onClick={() => setOpen(true)}
              className="relative flex h-11 w-11 items-center justify-center rounded-full border border-brand/60 text-brand transition-colors duration-300 hover:border-brand hover:bg-brand/10 lg:hidden"
              aria-label="Abrir menu"
              data-testid="mobile-menu-button"
            >
              <span className="absolute inset-0 animate-ping rounded-full border border-brand/30 [animation-duration:2.6s]" />
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="honeycomb !fixed inset-0 z-[70] flex flex-col bg-ink"
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <Logo onClick={() => go("#inicio")} />
              <button
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-metal text-paper transition-colors duration-300 hover:border-brand hover:text-brand"
                aria-label="Fechar menu"
                data-testid="mobile-menu-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {nav.map((item, i) => (
                <motion.button
                  key={item.href}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35 }}
                  onClick={() => go(item.href)}
                  className="border-b border-metal/60 py-4 text-left font-display text-2xl font-extrabold text-paper transition-colors duration-300 hover:text-brand"
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-industrial w-full"
                data-testid="mobile-menu-quote-button"
              >
                Solicitar Orçamento
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <p className="mt-5 text-center text-xs font-semibold tracking-[0.18em] text-steel">
                {site.schedule} • {site.region}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
